import { observer } from "mobx-react";
import { Toast } from "primereact/toast";
import React, { ReactNode } from "react";
import { useLocStore } from "../../localization";
import { LocContext } from "../../localization/LocContext";
import { withWSErrorContainer } from "../../error/withWSErrorContainer";
import styled from "styled-components";

const life = 3000;

const ChangesSuccessToast = observer(() => {
  const L = useLocStore(LocContext.GENERAL);

  return <>{L.id("d0ff9e20d8b2").loc("Endringene er lagret")}</>;
});

// A simple in-memory queue to survive unmounts/remounts of the Toast component
// This ensures toasts are not lost during UI refreshes (e.g., query invalidations)
interface QueuedToast {
  severity: "success" | "info" | "warn" | "error";
  detail?: ReactNode;
  summary?: ReactNode;
  life?: number;
}

const toastQueue: QueuedToast[] = [];

function enqueueToast(t: QueuedToast) {
  toastQueue.push(t);
}

function flushToastQueue() {
  if (!toastRef) return;
  try {
    while (toastQueue.length > 0) {
      const t = toastQueue.shift()!;
      toastRef.show({
        severity: t.severity,
        detail: t.detail,
        summary: t.summary,
        life: t.life ?? life,
      });
    }
  } catch (e) {
    // If something goes wrong during flush, keep remaining items
    // and log for diagnostics without crashing the UI
    // eslint-disable-next-line no-console
    console.debug("[CvpToast] Failed to flush toast queue", e);
  }
}

export const showToastMsg = (
  detail: string | ReactNode,
  severity: "success" | "info" | "warn" | "error" = "success"
) => {
  const payload: QueuedToast = {
    severity: severity ?? "success",
    detail: detail ?? <ChangesSuccessToast />,
    life: severity === "success" ? life : life * 3,
  };
  if (toastRef) {
    toastRef.show(payload);
  } else {
    enqueueToast(payload);
  }
};

export const showErrorToastMsg = (err, summary?: ReactNode) => {
  const text = err?.response?.data ?? err?.message ?? err;
  const payload: QueuedToast = {
    severity: "error",
    summary,
    detail: text,
    life,
  };
  if (toastRef) {
    toastRef.show(payload);
  } else {
    enqueueToast(payload);
  }
};

// storing ref globally is generally a bad idea,
// but in this particular usecase it made things easier
let toastRef: Toast | null = null;

export const CvpToast = withWSErrorContainer(() => {
  return (
    <StyledToast
      position="top-right"
      className="cvp-toast"
      ref={(ref) => {
        toastRef = ref;
        // Flush any queued toasts when the Toast component (re)mounts
        if (toastRef) {
          flushToastQueue();
        }
      }}
    />
  );
});

const StyledToast = styled(Toast)`
  &.cvp-toast {
    max-width: min(25rem, calc(100vw - 2rem));
    right: 1rem !important;
  }
`;
